import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

const questions = [
  {
    key: "patients",
    question: "How many new patients does your clinic get each month?",
    options: ["0–10", "10–25", "25–50", "50+"],
  },
  {
    key: "visibility",
    question: "When someone searches for a dentist in your city, where do you show up?",
    options: ["Top of Google", "Page 1, but not the top", "Page 2 or lower", "Not sure"],
  },
  {
    key: "challenge",
    question: "What is your biggest growth challenge right now?",
    options: ["Not enough leads", "Leads don't book", "Weak online reviews", "No time for marketing"],
  },
];

const GrowthCheck = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [form, setForm] = useState({ name: "", practice: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const answerQuestion = (key: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setStep((s) => s + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const summary = Object.entries(answers)
      .map(([k, v]) => `${k}: ${v}`)
      .join(" | ");
    // Only load the lead-delivery client when the visitor submits the form.
    try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { error } = await supabase.from("dental_leads").insert({
      name: form.name.trim(),
      practice: form.practice.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      source: `growth-check — ${summary}`,
    });
    if (error) throw error;
    setDone(true);
    } catch {
      toast({
        title: "Something went wrong",
        description: "We couldn't send your answers. Please call 772-202-2664 or try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = questions.length + 1;

  return (
    <section id="growth-check" className="section-padding section-gray">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <p className="text-primary font-semibold tracking-[0.2em] uppercase text-sm mb-4">
            60-Second Growth Check
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4 text-graphite">
            Where is your clinic <span className="gold-gradient-text">losing patients?</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Answer three quick questions and get an honest read on your growth potential.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="card-glass-light p-8 md:p-10"
        >
          {/* Progress */}
          <div className="flex gap-2 mb-8">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                  i < step || done ? "bg-primary" : "bg-border"
                }`}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-6"
              >
                <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="font-heading text-2xl mb-2 text-graphite">You're all set.</h3>
                <p className="text-muted-foreground">
                  We'll review your answers and your clinic's online presence, then reach out within 24 hours with an honest breakdown.
                </p>
              </motion.div>
            ) : step < questions.length ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
                  Question {step + 1} of {questions.length}
                </p>
                <h3 className="font-heading text-xl md:text-2xl mb-6 text-graphite">
                  {questions[step].question}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {questions[step].options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => answerQuestion(questions[step].key, opt)}
                      className="text-left border-2 border-border rounded-xl px-5 py-4 font-semibold text-graphite hover:border-primary hover:bg-primary/5 transition-colors"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <h3 className="font-heading text-xl md:text-2xl mb-2 text-graphite">
                  Where should we send your growth breakdown?
                </h3>
                <Input
                  placeholder="Your Name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                  maxLength={100}
                  className="bg-background border-border"
                />
                <Input
                  placeholder="Practice Name"
                  value={form.practice}
                  onChange={(e) => setForm((p) => ({ ...p, practice: e.target.value }))}
                  required
                  maxLength={100}
                  className="bg-background border-border"
                />
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  required
                  maxLength={20}
                  className="bg-background border-border"
                />
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  required
                  maxLength={255}
                  className="bg-background border-border"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground font-bold py-3 text-base hover:opacity-90"
                >
                  {loading ? "Sending..." : (
                    <>Get My Dental Growth Audit <Send className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
                <p className="text-muted-foreground text-xs text-center">
                  No obligation. No spam. Just a clear look at where your next patients come from.
                </p>
              </motion.form>
            )}
          </AnimatePresence>

          {!done && step > 0 && step <= questions.length && (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
            >
              <ArrowRight className="w-3 h-3 rotate-180" /> Back
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default GrowthCheck;
